import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Box,
    Alert,
} from '@mui/material';
import WarningIcon from '@mui/icons-material/Warning';
import { ActionButton, CancelButton } from '../Button';

interface PasswordExpiryDialogProps {
    open: boolean;
    onClose: () => void;
    onSkip: () => void;
    onChangePassword: () => void;
    daysUntilExpiry: number;
    isPasswordExpired: boolean;
}

const PasswordExpiryDialog: React.FC<PasswordExpiryDialogProps> = ({
    open,
    onClose,
    onSkip,
    onChangePassword,
    daysUntilExpiry,
    isPasswordExpired,
}) => {
    const getAlertMessage = () => {
        if (isPasswordExpired) {
            return 'Your password has expired. Please change your password to continue.';
        }
        return `Your password is expiring in ${daysUntilExpiry} day${daysUntilExpiry === 1 ? '' : 's'}.`;
    };

    const getAlertSeverity = () => {
        return isPasswordExpired ? 'error' : 'warning';
    };

    return (
        <Dialog
            open={open}
            onClose={() => {}} // Prevent closing by clicking outside or pressing escape
            maxWidth="sm"
            fullWidth
            disableEscapeKeyDown // Prevent closing with escape key
            PaperProps={{
                sx: {
                    borderRadius: '16px',
                    p: 0,
                }
            }}
        >
            <DialogTitle
                sx={{
                    fontFamily: 'Poppins, sans-serif',
                    fontSize: 20,
                    fontWeight: 600,
                    color: '#132F5F',
                    textAlign: 'center',
                    pb: 1,
                }}
            >
                Password Expiry Notice
            </DialogTitle>

            <DialogContent sx={{ px: 3, py: 2 }}>
                <Box sx={{ textAlign: 'center' }}>
                    <Alert
                        severity={getAlertSeverity()}
                        icon={<WarningIcon />}
                        sx={{
                            mb: 3,
                            fontFamily: 'Poppins, sans-serif',
                            borderRadius: '12px',
                            '& .MuiAlert-message': {
                                fontSize: { xs: 13, sm: 14 },
                                fontWeight: 500,
                                lineHeight: 1.4,
                                textAlign: 'left',
                            },
                            '& .MuiAlert-icon': {
                                fontSize: { xs: 20, sm: 22 },
                                mr: 1.5,
                            },
                        }}
                    >
                        {getAlertMessage()}
                    </Alert>
                </Box>
            </DialogContent>

            <DialogActions sx={{ 
                px: 3, 
                py: 2, 
                gap: 2,
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: 'stretch',
                justifyContent: 'center',
                '& > :not(style) ~ :not(style)': {
                    marginLeft: { xs: 0, sm: '8px' },
                    marginTop: { xs: '8px', sm: 0 },
                }
            }}>
                {!isPasswordExpired && (
                    <CancelButton
                        onClick={onSkip}
                        sx={{
                            borderColor: '#666',
                            color: '#666',
                            borderRadius: '12px',
                            px: 3,
                            py: 1.5,
                            fontFamily: 'Poppins, sans-serif',
                            fontWeight: 500,
                            fontSize: 14,
                            width: { xs: '100%', sm: '180px' },
                            order: { xs: 2, sm: 1 },
                            '&:hover': {
                                borderColor: '#666',
                                backgroundColor: 'transparent',
                            },
                        }}
                    >
                        Skip
                    </CancelButton>
                )}
                
                <ActionButton
                    onClick={onChangePassword}
                    sx={{
                        backgroundColor: '#001F5B',
                        px: 4,
                        py: 1.5,
                        fontFamily: 'Poppins, sans-serif',
                        fontWeight: 500,
                        fontSize: 14,
                        width: { xs: '100%', sm: '180px' },
                        order: { xs: 1, sm: 2 },
                        '&:hover': {
                            backgroundColor: '#001742',
                        },
                    }}
                >
                    Change Password
                </ActionButton>
            </DialogActions>
        </Dialog>
    );
};

export default PasswordExpiryDialog;
